// CLASSIC LOOK (v1.70): pre-Floodlight copy from ea44aec, used by
// screens/classic/CommunityScreen.jsx. Keep behaviour in step with
// components/community/ChannelsSidebar.jsx (the Floodlight version).
import { useState, useCallback, useMemo, memo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../../../context/ThemeContext";

// ─── Constants ────────────────────────────────────────────────────────────────

const BRAND = "#A259FF";
const SIDEBAR_W = 220;

// ─── Sub-components ───────────────────────────────────────────────────────────

const CategoryRow = memo(function CategoryRow({ category, collapsed, onToggle }) {
  const handlePress = useCallback(() => onToggle(category.id), [onToggle, category.id]);
  return (
    <TouchableOpacity
      style={styles.categoryRow}
      onPress={handlePress}
      activeOpacity={0.7}
      accessibilityRole="button"
      aria-expanded={!collapsed}
      accessibilityLabel={`${category.label} channels`}
    >
      <Ionicons
        name={collapsed ? "chevron-forward" : "chevron-down"}
        size={10}
        color="rgba(255,255,255,0.35)"
        style={{ marginRight: 4 }}
      />
      <Text style={styles.categoryLabel} numberOfLines={1}>
        {category.label}
      </Text>
    </TouchableOpacity>
  );
});

const ChannelRow = memo(function ChannelRow({ channel, selected, onPress }) {
  const [hovered, setHovered] = useState(false);
  const isActive = selected === channel.id;

  const webHandlers = useMemo(
    () =>
      Platform.OS === "web"
        ? { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) }
        : {},
    [],
  );

  const iconColor = isActive ? "#fff" : hovered ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.4)";
  const nameColor = isActive ? "rgba(255,255,255,0.95)" : hovered ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.45)";

  return (
    <TouchableOpacity
      style={[
        styles.channelRow,
        isActive && styles.channelRowActive,
        !isActive && hovered && styles.channelRowHover,
      ]}
      dataSet={{ crow: isActive ? "active" : "idle" }}
      onPress={() => onPress(channel.id)}
      activeOpacity={0.8}
      accessibilityRole="button"
      aria-selected={!!isActive}
      accessibilityLabel={`Channel ${channel.displayName || channel.name || channel.id}`}
      {...webHandlers}
    >
      {/* Active accent bar */}
      {isActive && <View style={styles.activeBar} />}

      {/* Icon */}
      <View style={styles.channelIcon}>
        {channel.type === "voice" ? (
          <Ionicons name="volume-medium-outline" size={16} color={iconColor} />
        ) : channel.locked ? (
          <Ionicons name="lock-closed-outline" size={14} color={iconColor} />
        ) : (
          <Text style={[styles.hashIcon, { color: iconColor }]}>#</Text>
        )}
      </View>

      {/* Name */}
      <Text style={[styles.channelName, { color: nameColor, fontWeight: isActive ? "600" : "500" }]} numberOfLines={1}>
        {channel.displayName ?? channel.name}
      </Text>

      {/* Right side: badge / voice count */}
      <View style={styles.channelRight}>
        {!!channel.badge && !isActive && (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{channel.badge > 99 ? "99+" : channel.badge}</Text>
          </View>
        )}
        {channel.type === "voice" && channel.members > 0 && (
          <View style={styles.voicePill}>
            <Ionicons name="people-outline" size={11} color="rgba(255,255,255,0.4)" />
            <Text style={styles.voiceCount}>{channel.members}</Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
});

const SubChannelRow = memo(function SubChannelRow({ subChannel, selected, onPress }) {
  const [hovered, setHovered] = useState(false);
  const isActive = selected === subChannel.id;

  const webHandlers = useMemo(
    () =>
      Platform.OS === "web"
        ? { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) }
        : {},
    [],
  );

  const nameColor = isActive ? "rgba(255,255,255,0.95)" : hovered ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.4)";

  return (
    <TouchableOpacity
      style={[
        styles.channelRow,
        styles.subRow,
        isActive && styles.channelRowActive,
        !isActive && hovered && styles.channelRowHover,
      ]}
      onPress={() => onPress(subChannel.id)}
      activeOpacity={0.8}
      accessibilityRole="button"
      aria-selected={!!isActive}
      accessibilityLabel={`Channel ${subChannel.displayName || subChannel.name || subChannel.id}`}
      {...webHandlers}
    >
      {isActive && <View style={styles.activeBar} />}
      <View style={styles.channelIcon}>
        <Text style={[styles.hashIcon, { color: nameColor, fontSize: 14 }]}>#</Text>
      </View>
      <Text style={[styles.channelName, { color: nameColor, fontSize: 13 }]} numberOfLines={1}>
        {subChannel.displayName ?? subChannel.name}
      </Text>
    </TouchableOpacity>
  );
});

// ─── Main component ───────────────────────────────────────────────────────────

export default function ChannelsSidebar({
  serverName = "BetOptimized",
  categories: categoriesProp,
  loading = false,
  selectedChannelId = null,
  onChannelSelect,
  error = null,
  style,
}) {
  const [localSelected, setLocalSelected] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const data = useMemo(
    () =>
      categoriesProp?.length
        ? categoriesProp.map((c) => ({ ...c, label: c.label ?? c.name ?? c.id }))
        : loading ? [] : [],
    [categoriesProp, loading],
  );

  const toggleCategory = useCallback((id) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleChannelPress = useCallback((id) => {
    setLocalSelected(id);
    onChannelSelect?.(id);
  }, [onChannelSelect]);

  const activeId = selectedChannelId ?? localSelected;

  return (
    <View style={[styles.sidebar, style]} accessibilityRole="navigation" accessibilityLabel="Channels">
      {/* Server header */}
      <View style={styles.serverHeader}>
        <View style={styles.serverLogo}>
          <Text style={styles.serverLogoText}>b°</Text>
        </View>
        <Text style={styles.serverName} numberOfLines={1}>{serverName}</Text>
      </View>

      {/* Divider */}
      <View style={styles.divider} />

      {/* Channel list */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={BRAND} />
        </View>
      ) : error || !data.length ? (
        <View style={styles.loadingContainer}>
          <Text style={{ color: "rgba(255,255,255,0.45)", fontSize: 12, textAlign: "center", paddingHorizontal: 16, lineHeight: 18 }}>
            {error ? "Channels couldn't load. Sign in, or try again in a moment." : "No channels yet."}
          </Text>
        </View>
      ) : (
        <ScrollView
          style={styles.channelList}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 12 }}
        >
          {data.map((category) => (
            <View key={category.id}>
              <CategoryRow
                category={category}
                collapsed={!!collapsed[category.id]}
                onToggle={toggleCategory}
              />
              {!collapsed[category.id] &&
                (category.channels ?? []).map((channel) => (
                  <View key={channel.id}>
                    <ChannelRow
                      channel={channel}
                      selected={activeId}
                      onPress={handleChannelPress}
                    />
                    {(channel.subChannels ?? []).map((sub) => (
                      <SubChannelRow
                        key={sub.id}
                        subChannel={sub}
                        selected={activeId}
                        onPress={handleChannelPress}
                      />
                    ))}
                  </View>
                ))}
            </View>
          ))}
        </ScrollView>
      )}
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  sidebar: {
    width: SIDEBAR_W,
    height: "100%",
    backgroundColor: "rgba(255,255,255,0.04)",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.08)",
    overflow: "hidden",
    flexDirection: "column",
  },

  // Server header
  serverHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingVertical: 14,
    gap: 10,
  },
  serverLogo: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: BRAND,
    alignItems: "center",
    justifyContent: "center",
  },
  serverLogoText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "800",
  },
  serverName: {
    flex: 1,
    color: "rgba(255,255,255,0.9)",
    fontSize: 14,
    fontWeight: "700",
    letterSpacing: 0.1,
  },

  divider: {
    height: 1,
    backgroundColor: "rgba(255,255,255,0.07)",
    marginHorizontal: 0,
  },

  channelList: {
    flex: 1,
    paddingTop: 6,
  },
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },

  // Category
  categoryRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingTop: 14,
    paddingBottom: 4,
    ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
  },
  categoryLabel: {
    flex: 1,
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.35)",
  },

  // Channel row
  channelRow: {
    flexDirection: "row",
    alignItems: "center",
    marginHorizontal: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    position: "relative",
    overflow: "hidden",
    ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
  },
  channelRowActive: {
    backgroundColor: "rgba(162,89,255,0.18)",
  },
  channelRowHover: {
    backgroundColor: "rgba(255,255,255,0.05)",
  },
  activeBar: {
    position: "absolute",
    left: 0,
    top: 6,
    bottom: 6,
    width: 3,
    borderRadius: 2,
    backgroundColor: BRAND,
  },
  subRow: {
    marginLeft: 18,
  },

  channelIcon: {
    width: 20,
    alignItems: "center",
    marginRight: 7,
  },
  hashIcon: {
    fontSize: 18,
    fontWeight: "600",
    lineHeight: 20,
  },
  channelName: {
    flex: 1,
    fontSize: 14,
    lineHeight: 18,
  },
  channelRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  badge: {
    backgroundColor: BRAND,
    borderRadius: 8,
    minWidth: 18,
    height: 18,
    paddingHorizontal: 5,
    alignItems: "center",
    justifyContent: "center",
  },
  badgeText: {
    color: "#fff",
    fontSize: 10,
    fontWeight: "700",
  },
  voicePill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },
  voiceCount: {
    fontSize: 11,
    color: "rgba(255,255,255,0.4)",
  },
});
